import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const TermsOfServicePage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-gray-900">
      <SEOHead 
        title="Terms of Service - AI BookMoney"
        description="Terms of Service for AI BookMoney platform. Review our terms and conditions for using our AI-powered KDP publishing tools and services."
        keywords="terms of service, AI BookMoney terms, legal terms, user agreement"
        canonical="/terms"
        ogTitle="Terms of Service - AI BookMoney"
        ogDescription="Terms of Service for AI BookMoney AI-powered publishing platform"
        ogImage="/ai-manuscript-demo.png"
        ogUrl="/terms"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Terms of Service",
          "description": "Terms of Service for AI BookMoney platform",
          "url": "https://aibookmoney.com/terms"
        }}
      />
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="flex items-center space-x-3 mb-4">
            <BookOpen className="h-8 w-8 text-purple-400" />
            <span className="text-2xl font-bold text-white">AI BookMoney</span>
          </div>
          
          <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
          <p className="text-gray-400 mt-2">Last updated: January 17, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-invert max-w-none">
          
          <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
          <p className="text-gray-300 mb-6">
            By accessing and using AI BookMoney (the &quot;Service&quot;), operated by JMFT LLC (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;), 
            you accept and agree to be bound by the terms and provision of this agreement.
          </p>

          <h2 className="text-2xl font-bold text-white mb-4">2. Description of Service</h2>
          <p className="text-gray-300 mb-4">
            AI BookMoney is an AI-powered platform that provides tools and services for creating books, covers, and metadata 
            optimization for Amazon Kindle Direct Publishing (KDP). Our services include:
          </p>
          <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
            <li>AI-powered book content generation</li>
            <li>Professional cover design tools</li>
            <li>Metadata optimization for KDP</li>
            <li>Publishing guidance and resources</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mb-4">3. User Accounts</h2>
          <p className="text-gray-300 mb-4">
            To use our Service, you must create an account. You are responsible for:
          </p>
          <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
            <li>Maintaining the confidentiality of your account information</li>
            <li>All activities that occur under your account</li>
            <li>Providing accurate and complete information</li>
            <li>Immediately notifying us of any unauthorized use of your account</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mb-4">4. Subscription and Billing</h2>
          <h3 className="text-xl font-semibold text-white mb-3">4.1 Free Trial</h3>
          <p className="text-gray-300 mb-4">
            New users receive a 14-day free trial with 40 credits. No credit card is required for the trial.
          </p>
          
          <h3 className="text-xl font-semibold text-white mb-3">4.2 Paid Subscription</h3>
          <p className="text-gray-300 mb-4">
            Our paid subscription is $19/month and includes 75 credits plus access to all tools. Additional credits 
            can be purchased separately.
          </p>
          
          <h3 className="text-xl font-semibold text-white mb-3">4.3 Credit System</h3>
          <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
            <li>Credits are consumed when using AI generation services</li>
            <li>Credits do not expire as long as your subscription is active</li>
            <li>Credit purchases are non-refundable</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mb-4">5. Refund Policy</h2>
          <p className="text-gray-300 mb-4">
            <strong className="text-white">All sales are final. We do not offer refunds for any purchases, including but not limited to:</strong>
          </p>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-2">
            <li>Monthly or yearly subscription fees</li>
            <li>Credit purchases (starter, creator, pro, or enterprise packages)</li>
            <li>Lifetime access purchases</li>
            <li>Any additional services or features</li>
          </ul>
          
          <p className="text-gray-300 mb-4">
            <strong className="text-white">Why No Refunds?</strong>
          </p>
          <p className="text-gray-300 mb-4">
            Our AI-powered services involve significant computational resources and third-party API costs that are incurred 
            immediately upon use. Once credits are used to generate content, covers, or optimize metadata, those resources 
            have been consumed and cannot be recovered.
          </p>
          
          <p className="text-gray-300 mb-4">
            <strong className="text-white">Free Trial Available:</strong>
          </p>
          <p className="text-gray-300 mb-4">
            We offer a 14-day free trial with 40 credits and no credit card required. This allows you to fully test our 
            platform and evaluate whether it meets your needs before making any purchase commitment.
          </p>
          
          <p className="text-gray-300 mb-4">
            <strong className="text-white">Subscription Cancellation:</strong>
          </p>
          <p className="text-gray-300 mb-6">
            You may cancel your subscription at any time through your account settings. Upon cancellation, you will retain 
            access to your paid subscription features until the end of your current billing period. No refunds will be 
            provided for unused portions of your subscription period or unused credits.
          </p>

          <h2 className="text-2xl font-bold text-white mb-4">6. Acceptable Use</h2>
          <p className="text-gray-300 mb-4">You agree not to use the Service to:</p>
          <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
            <li>Create content that violates copyright, trademark, or other intellectual property rights</li>
            <li>Generate illegal, harmful, threatening, abusive, or defamatory content</li>
            <li>Create spam or misleading content</li>
            <li>Reverse engineer or attempt to extract source code from our AI models</li>
            <li>Resell or redistribute our services without written permission</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mb-4">7. Intellectual Property</h2>
          <h3 className="text-xl font-semibold text-white mb-3">7.1 Your Content</h3>
          <p className="text-gray-300 mb-4">
            You retain ownership of content you create using our tools. However, you grant us a limited license to 
            process and generate content using our AI systems.
          </p>
          
          <h3 className="text-xl font-semibold text-white mb-3">7.2 Generated Content</h3>
          <p className="text-gray-300 mb-6">
            AI-generated content is provided &quot;as-is.&quot; While we strive for originality, you are responsible for ensuring 
            your final published content complies with all applicable laws and platform requirements.
          </p>

          <h2 className="text-2xl font-bold text-white mb-4">8. Disclaimers</h2>
          <p className="text-gray-300 mb-6">
            THE SERVICE IS PROVIDED &quot;AS IS&quot; WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE THAT YOUR BOOKS 
            WILL GENERATE INCOME OR ACHIEVE SPECIFIC RANKINGS ON AMAZON KDP OR OTHER PLATFORMS.
          </p>

          <h2 className="text-2xl font-bold text-white mb-4">9. Limitation of Liability</h2>
          <p className="text-gray-300 mb-6">
            IN NO EVENT SHALL JMFT LLC BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE 
            DAMAGES ARISING OUT OF YOUR USE OF THE SERVICE.
          </p>

          <h2 className="text-2xl font-bold text-white mb-4">10. Termination</h2>
          <p className="text-gray-300 mb-6">
            We reserve the right to terminate or suspend your account at any time for violation of these terms. 
            You may cancel your subscription at any time through your account settings.
          </p>

          <h2 className="text-2xl font-bold text-white mb-4">11. Changes to Terms</h2>
          <p className="text-gray-300 mb-6">
            We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. 
            Continued use of the Service constitutes acceptance of modified terms.
          </p>

          <h2 className="text-2xl font-bold text-white mb-4">12. Contact Information</h2>
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <p className="text-gray-300 mb-2"><strong className="text-white">JMFT LLC</strong></p>
            <p className="text-gray-300 mb-2">Email: legal@aibookmoney.com</p>
            <p className="text-gray-300">Support: support@aibookmoney.com</p>
          </div>

          <div className="border-t border-gray-700 pt-6">
            <p className="text-gray-400 text-sm">
              These Terms of Service are governed by the laws of the United States. 
              Any disputes shall be resolved in accordance with the jurisdiction where JMFT LLC is registered.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;